import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../Providers/LanguageContext';
import styles from './TermsOfService.module.css';

export function TermsOfService() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  return (
    <div className={styles.termsContainer}>
      <button
        className={styles.backButton}
        onClick={() => navigate(-1)}
        aria-label={t('common.back')}
      >
        ← {t('common.back')}
      </button>
      <div className={styles.header}>
        <h1>{t('terms.title')}</h1>
        <p className={styles.lastUpdated}>
          {t('terms.lastUpdated')}: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </div>

      <div className={styles.content}>
        {/* 1. Acceptance of Terms */}
        <section className={styles.section}>
          <h2>{t('terms.acceptance.title')}</h2>
          <p>{t('terms.acceptance.p1')}</p>
          <p>{t('terms.acceptance.p2')}</p>
        </section>

        {/* 2. Description of Service */}
        <section className={styles.section}>
          <h2>{t('terms.description.title')}</h2>
          <p>{t('terms.description.p1')}</p>
          <ul>
            <li>{t('terms.description.feature1')}</li>
            <li>{t('terms.description.feature2')}</li>
            <li>{t('terms.description.feature3')}</li>
            <li>{t('terms.description.feature4')}</li>
            <li>{t('terms.description.feature5')}</li>
            <li>{t('terms.description.feature6')}</li>
          </ul>
        </section>

        {/* 3. User Accounts */}
        <section className={styles.section}>
          <h2>{t('terms.accounts.title')}</h2>

          <h3>{t('terms.accounts.eligibility.title')}</h3>
          <p>{t('terms.accounts.eligibility.p1')}</p>

          <h3>{t('terms.accounts.responsibilities.title')}</h3>
          <p>{t('terms.accounts.responsibilities.p1')}</p>
          <ul>
            <li>{t('terms.accounts.responsibilities.item1')}</li>
            <li>{t('terms.accounts.responsibilities.item2')}</li>
            <li>{t('terms.accounts.responsibilities.item3')}</li>
          </ul>
          <p>{t('terms.accounts.responsibilities.p2')}</p>

          <h3>{t('terms.accounts.prohibited.title')}</h3>
          <p>{t('terms.accounts.prohibited.p1')}</p>
          <ul>
            <li>{t('terms.accounts.prohibited.item1')}</li>
            <li>{t('terms.accounts.prohibited.item2')}</li>
            <li>{t('terms.accounts.prohibited.item3')}</li>
            <li>{t('terms.accounts.prohibited.item4')}</li>
            <li>{t('terms.accounts.prohibited.item5')}</li>
            <li>{t('terms.accounts.prohibited.item6')}</li>
          </ul>
          <p>{t('terms.accounts.prohibited.p2')}</p>
        </section>

        {/* 4. Premium Subscriptions */}
        <section className={styles.section}>
          <h2>{t('terms.premium.title')}</h2>

          <h3>{t('terms.premium.billing.title')}</h3>
          <p>{t('terms.premium.billing.p1')}</p>
          <p>{t('terms.premium.billing.p2')}</p>
          <ul>
            <li>{t('terms.premium.billing.item1')}</li>
            <li>{t('terms.premium.billing.item2')}</li>
          </ul>
          <p>{t('terms.premium.billing.p3')}</p>

          <h3>{t('terms.premium.cancellation.title')}</h3>
          <p>{t('terms.premium.cancellation.p1')}</p>
          <p>{t('terms.premium.cancellation.p2')}</p>

          <h3>{t('terms.premium.refunds.title')}</h3>
          <p>{t('terms.premium.refunds.p1')}</p>
          <p>{t('terms.premium.refunds.p2')}</p>
          <p>{t('terms.premium.refunds.p3')}</p>
        </section>

        {/* 5. Acceptable Use Policy */}
        <section className={styles.section}>
          <h2>{t('terms.acceptable.title')}</h2>
          <p>{t('terms.acceptable.p1')}</p>
          <ul>
            <li>{t('terms.acceptable.item1')}</li>
            <li>{t('terms.acceptable.item2')}</li>
            <li>{t('terms.acceptable.item3')}</li>
            <li>{t('terms.acceptable.item4')}</li>
            <li>{t('terms.acceptable.item5')}</li>
            <li>{t('terms.acceptable.item6')}</li>
          </ul>
          <p>{t('terms.acceptable.p2')}</p>
        </section>

        {/* 6. Intellectual Property Rights */}
        <section className={styles.section}>
          <h2>{t('terms.intellectual.title')}</h2>

          <h3>{t('terms.intellectual.ownership.title')}</h3>
          <p>{t('terms.intellectual.ownership.p1')}</p>
          <p>{t('terms.intellectual.ownership.p2')}</p>

          <h3>{t('terms.intellectual.userContent.title')}</h3>
          <p>{t('terms.intellectual.userContent.p1')}</p>
          <p>{t('terms.intellectual.userContent.p2')}</p>
        </section>

        {/* 7. Disclaimers and Limitations of Liability */}
        <section className={styles.section}>
          <h2>{t('terms.disclaimers.title')}</h2>
          <p className={styles.legalCaps}>{t('terms.disclaimers.p1')}</p>
          <p>{t('terms.disclaimers.p2')}</p>
          <ul>
            <li>{t('terms.disclaimers.item1')}</li>
            <li>{t('terms.disclaimers.item2')}</li>
            <li>{t('terms.disclaimers.item3')}</li>
            <li>{t('terms.disclaimers.item4')}</li>
          </ul>
          <p className={styles.legalCaps}>{t('terms.disclaimers.p3')}</p>
          <ul>
            <li>{t('terms.disclaimers.item5')}</li>
            <li>{t('terms.disclaimers.item6')}</li>
            <li>{t('terms.disclaimers.item7')}</li>
            <li>{t('terms.disclaimers.item8')}</li>
          </ul>
        </section>

        {/* 8. Privacy */}
        <section className={styles.section}>
          <h2>{t('terms.privacy.title')}</h2>
          <p>{t('terms.privacy.p1')}</p>
          <p>{t('terms.privacy.p2')}</p>
          <p>
            <a href="/privacy" className={styles.link}>
              {t('terms.viewPrivacyPolicy')}
            </a>
          </p>
        </section>

        {/* 9. Changes to Terms */}
        <section className={styles.section}>
          <h2>{t('terms.changes.title')}</h2>
          <p>{t('terms.changes.p1')}</p>
          <ul>
            <li>{t('terms.changes.item1')}</li>
            <li>{t('terms.changes.item2')}</li>
            <li>{t('terms.changes.item3')}</li>
          </ul>
          <p>{t('terms.changes.p2')}</p>
        </section>

        {/* 10. Governing Law */}
        <section className={styles.section}>
          <h2>{t('terms.governing.title')}</h2>
          <p>{t('terms.governing.p1')}</p>
          <p>{t('terms.governing.p2')}</p>
        </section>

        {/* 11. Contact Information */}
        <section className={styles.section}>
          <h2>{t('terms.contact.title')}</h2>
          <p>{t('terms.contact.p1')}</p>
          <p>
            <strong>{t('terms.contact.email')}</strong> {t('common.contactEmail')}
          </p>
          <p>
            <strong>{t('terms.contact.company')}</strong> {t('common.companyName')}
            <br />
            {t('common.companyCountry')}
          </p>
        </section>

        {/* 12. Severability */}
        <section className={styles.section}>
          <h2>{t('terms.severability.title')}</h2>
          <p>{t('terms.severability.p1')}</p>
        </section>

        {/* Acknowledgment */}
        <section className={styles.section}>
          <p className={styles.acknowledgment}>
            {t('terms.acknowledgment')}
          </p>
        </section>
      </div>
    </div>
  );
}
